import { AlertTriangle, CheckCircle2, KeyRound, Loader2, Mail, ShieldCheck, UserRound } from 'lucide-react';
import { useEffect, useState, type ReactNode } from 'react';
import { languageLabels, supportedLanguages, useI18n } from '../i18n/LanguageContext';
import { useApiClient } from '../lib/apiClient';

type SupportedLanguage = 'en' | 'es' | 'it';
type PortalAccessStatus = 'NOT_ACTIVE' | 'PENDING_AUTH0' | 'INVITED' | 'ACTIVE';
type EmailDeliveryMode = 'webhook' | 'ses' | 'mock';

type PortalAccessPayload = {
  patientId: string;
  portalEnabled: boolean;
  auth0ManagementEnabled: boolean;
  loginUrl: string;
  status: PortalAccessStatus;
  patientEmail: string | null;
  preferredLanguage: SupportedLanguage | null;
  link: {
    id: string;
    createdAt: string;
    updatedAt: string;
  } | null;
  user: {
    id: string;
    email: string;
    firstName: string;
    lastName: string;
    active: boolean;
    mustChangePassword: boolean;
    auth0Provisioned: boolean;
    preferredLanguage: SupportedLanguage | null;
    passwordLastChangedAt: string | null;
    createdAt: string;
    updatedAt: string;
  } | null;
};

type PortalAccessActionPayload = PortalAccessPayload & {
  emailDeliveryMode: EmailDeliveryMode;
  createdUser: boolean;
  auth0Created: boolean;
  linkCreated: boolean;
};

function formatDateTime(value?: string | null) {
  if (!value) return '-';
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return '-';
  return parsed.toLocaleString([], {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

function statusTone(status: PortalAccessStatus) {
  if (status === 'ACTIVE') return 'bg-emerald-50 border-emerald-200 text-emerald-700';
  if (status === 'INVITED') return 'bg-amber-50 border-amber-200 text-amber-700';
  if (status === 'PENDING_AUTH0') return 'bg-sky-50 border-sky-200 text-sky-700';
  return 'bg-slate-100 border-slate-200 text-slate-700';
}

function statusLabel(status: PortalAccessStatus) {
  if (status === 'ACTIVE') return 'Portal access active';
  if (status === 'INVITED') return 'Credentials sent';
  if (status === 'PENDING_AUTH0') return 'Cognito provisioning pending';
  return 'Portal access not active';
}

function Banner({ kind, text }: { kind: 'success' | 'error' | 'info'; text: string }) {
  const tone =
    kind === 'success'
      ? 'border-emerald-200 bg-emerald-50 text-emerald-800'
      : kind === 'error'
        ? 'border-rose-200 bg-rose-50 text-rose-800'
        : 'border-sky-200 bg-sky-50 text-sky-800';
  const Icon = kind === 'success' ? CheckCircle2 : AlertTriangle;

  return (
    <div className={`flex items-start gap-2 rounded-xl border px-3 py-2 text-sm ${tone}`}>
      <Icon size={16} className="mt-0.5 shrink-0" />
      <span>{text}</span>
    </div>
  );
}

function DetailCard({ icon, label, value }: { icon: ReactNode; label: string; value: string }) {
  return (
    <div className="rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3">
      <div className="flex items-center gap-2 text-xs uppercase tracking-[0.16em] text-slate-500">
        {icon}
        <span>{label}</span>
      </div>
      <div className="mt-2 text-sm font-medium text-slate-900">{value}</div>
    </div>
  );
}

export function PatientPortalAccessModule({ patientId }: { patientId: string }) {
  const { tr } = useI18n();
  const { fetchApi } = useApiClient();
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [status, setStatus] = useState<PortalAccessPayload | null>(null);
  const [portalEmail, setPortalEmail] = useState('');
  const [preferredLanguage, setPreferredLanguage] = useState<SupportedLanguage>('en');
  const [notice, setNotice] = useState<{ kind: 'success' | 'error' | 'info'; text: string } | null>(null);

  async function readErrorMessage(response: Response) {
    const bodyText = await response.text().catch(() => '');
    if (!bodyText) return `${tr('Request failed')} (${response.status})`;
    try {
      const parsed = JSON.parse(bodyText) as { message?: string; error?: string };
      return parsed.message || parsed.error || `${tr('Request failed')} (${response.status})`;
    } catch {
      return bodyText;
    }
  }

  async function loadStatus() {
    setLoading(true);
    try {
      const response = await fetchApi(`/patients/${patientId}/portal-access`);
      if (!response.ok) {
        throw new Error(await readErrorMessage(response));
      }
      const payload = (await response.json()) as PortalAccessPayload;
      setStatus(payload);
      setPortalEmail(payload.user?.email || payload.patientEmail || '');
      setPreferredLanguage(payload.user?.preferredLanguage || payload.preferredLanguage || 'en');
      setNotice(null);
    } catch (error) {
      const message = error instanceof Error ? error.message : tr('Failed to load portal access');
      setNotice({ kind: 'error', text: message });
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    void loadStatus();
  }, [patientId]);

  async function activatePortalAccess() {
    setSubmitting(true);
    try {
      const response = await fetchApi(`/patients/${patientId}/portal-access/activate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email: portalEmail.trim() || undefined,
          preferredLanguage,
        }),
      });
      if (!response.ok) {
        throw new Error(await readErrorMessage(response));
      }
      const payload = (await response.json()) as PortalAccessActionPayload;
      setStatus(payload);
      setPortalEmail(payload.user?.email || payload.patientEmail || '');
      setPreferredLanguage(payload.user?.preferredLanguage || payload.preferredLanguage || 'en');
      setNotice({
        kind: payload.emailDeliveryMode === 'mock' ? 'info' : 'success',
        text:
          payload.emailDeliveryMode === 'mock'
            ? tr('Portal access was activated, but email delivery is currently in mock mode.')
            : tr('Portal credentials were sent successfully.'),
      });
    } catch (error) {
      const message = error instanceof Error ? error.message : tr('Failed to activate portal access');
      setNotice({ kind: 'error', text: message });
    } finally {
      setSubmitting(false);
    }
  }

  async function resetCredentials() {
    setSubmitting(true);
    try {
      const response = await fetchApi(`/patients/${patientId}/portal-access/reset-credentials`, {
        method: 'POST',
      });
      if (!response.ok) {
        throw new Error(await readErrorMessage(response));
      }
      const payload = (await response.json()) as PortalAccessActionPayload;
      setStatus(payload);
      setPortalEmail(payload.user?.email || payload.patientEmail || '');
      setPreferredLanguage(payload.user?.preferredLanguage || payload.preferredLanguage || 'en');
      setNotice({
        kind: payload.emailDeliveryMode === 'mock' ? 'info' : 'success',
        text:
          payload.emailDeliveryMode === 'mock'
            ? tr('A temporary password was generated, but email delivery is currently in mock mode.')
            : tr('New portal credentials were sent successfully.'),
      });
    } catch (error) {
      const message = error instanceof Error ? error.message : tr('Failed to send portal credentials');
      setNotice({ kind: 'error', text: message });
    } finally {
      setSubmitting(false);
    }
  }

  if (loading && !status) {
    return (
      <div className="flex items-center gap-2 rounded-2xl border border-slate-200 bg-white px-4 py-6 text-sm text-slate-500">
        <Loader2 size={16} className="animate-spin" />
        {tr('Loading portal access...')}
      </div>
    );
  }

  const canProvision = Boolean(status?.auth0ManagementEnabled);
  const emailLocked = Boolean(status?.user?.email);
  const needsActivation = status?.status === 'NOT_ACTIVE' || status?.status === 'PENDING_AUTH0';

  return (
    <div className="space-y-4">
      {notice ? <Banner kind={notice.kind} text={notice.text} /> : null}

      {!status?.portalEnabled ? (
        <Banner kind="info" text={tr('Patient portal routes are disabled in this environment. Activation can be prepared, but portal sign-in will remain unavailable until the portal is enabled.')} />
      ) : null}

      {!canProvision ? (
        <Banner kind="error" text={tr('Cognito user provisioning is disabled. Enable it before activating portal access.')} />
      ) : null}

      {status ? (
        <>
          <div className="grid gap-3 md:grid-cols-4">
            <DetailCard icon={<ShieldCheck size={14} />} label={tr('Status')} value={tr(statusLabel(status.status))} />
            <DetailCard icon={<Mail size={14} />} label={tr('Portal email')} value={status.user?.email || status.patientEmail || '-'} />
            <DetailCard icon={<UserRound size={14} />} label={tr('Cognito')} value={status.user?.auth0Provisioned ? tr('Provisioned') : tr('Not provisioned')} />
            <DetailCard icon={<KeyRound size={14} />} label={tr('Password state')} value={status.user?.mustChangePassword ? tr('Password reset required') : tr('Ready')} />
          </div>

          <div className="rounded-3xl border border-slate-200 bg-white p-5 shadow-sm">
            <div className="flex flex-col gap-3 md:flex-row md:items-start md:justify-between">
              <div>
                <p className="text-xs font-semibold uppercase tracking-[0.18em] text-sky-700">{tr('Portal Access')}</p>
                <h2 className="mt-1 text-xl font-semibold text-slate-900">{tr('Patient portal identity')}</h2>
                <p className="mt-1 max-w-3xl text-sm text-slate-600">
                  {needsActivation
                    ? tr('Create the patient portal account, provision the Cognito identity, and send a temporary password.')
                    : tr('Send a new temporary password to the linked patient portal account.')}
                </p>
              </div>
              <span className={`inline-flex w-fit rounded-full border px-3 py-1 text-xs font-semibold ${statusTone(status.status)}`}>
                {tr(statusLabel(status.status))}
              </span>
            </div>

            <div className="mt-5 grid gap-4 md:grid-cols-2">
              <label className="text-sm">
                <span className="text-slate-500">{tr('Portal email')}</span>
                <input
                  type="email"
                  value={portalEmail}
                  disabled={emailLocked}
                  onChange={(event) => setPortalEmail(event.target.value)}
                  className="mt-1 w-full rounded-xl border border-slate-200 px-3 py-2 disabled:bg-slate-50 disabled:text-slate-500"
                  placeholder={tr('patient@example.com')}
                />
              </label>

              <label className="text-sm">
                <span className="text-slate-500">{tr('Portal language')}</span>
                <select
                  value={preferredLanguage}
                  onChange={(event) => setPreferredLanguage(event.target.value as SupportedLanguage)}
                  className="mt-1 w-full rounded-xl border border-slate-200 bg-white px-3 py-2"
                >
                  {supportedLanguages.map((language) => (
                    <option key={language} value={language}>
                      {languageLabels[language]}
                    </option>
                  ))}
                </select>
              </label>
            </div>

            {emailLocked ? (
              <p className="mt-3 text-xs text-slate-500">
                {tr('The portal email is locked after activation to avoid desynchronizing the linked Cognito identity.')}
              </p>
            ) : null}

            <div className="mt-5 flex flex-wrap gap-3">
              <button
                type="button"
                disabled={submitting || !canProvision || !portalEmail.trim()}
                onClick={() => {
                  void (needsActivation ? activatePortalAccess() : resetCredentials());
                }}
                className="inline-flex items-center gap-2 rounded-xl bg-ink px-4 py-2 text-sm font-medium text-white disabled:cursor-not-allowed disabled:opacity-60"
              >
                {submitting ? <Loader2 size={15} className="animate-spin" /> : <KeyRound size={15} />}
                {tr(needsActivation ? 'Activate portal access' : 'Send new credentials')}
              </button>

              {needsActivation ? null : (
                <a
                  href={status.loginUrl}
                  target="_blank"
                  rel="noreferrer"
                  className="inline-flex items-center gap-2 rounded-xl border border-slate-200 px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50"
                >
                  <ShieldCheck size={15} />
                  {tr('Open portal login')}
                </a>
              )}
            </div>

            {status.user ? (
              <div className="mt-6 grid gap-3 md:grid-cols-2">
                <div className="rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-700">
                  <div className="font-medium text-slate-900">{tr('Linked portal user')}</div>
                  <div className="mt-2 space-y-1">
                    <div>{`${status.user.firstName} ${status.user.lastName}`}</div>
                    <div>{status.user.email}</div>
                    <div>{tr(status.user.active ? 'Active user' : 'Inactive user')}</div>
                  </div>
                </div>
                <div className="rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-700">
                  <div className="font-medium text-slate-900">{tr('Access timeline')}</div>
                  <div className="mt-2 space-y-1">
                    <div>{`${tr('Linked')}: ${formatDateTime(status.link?.createdAt)}`}</div>
                    <div>{`${tr('Last password change')}: ${formatDateTime(status.user.passwordLastChangedAt)}`}</div>
                    <div>{`${tr('Last account update')}: ${formatDateTime(status.user.updatedAt)}`}</div>
                  </div>
                </div>
              </div>
            ) : null}
          </div>
        </>
      ) : null}
    </div>
  );
}
